import React, { useState } from "react";
import { Alert, Box, Button, IconButton, InputAdornment, Paper, Stack, TextField, Typography } from "@mui/material";
import VisibilityIcon from "@mui/icons-material/Visibility";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";
import { useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "../auth/AuthContext";

export default function Login() {
    const { login } = useAuth();
    const navigate = useNavigate();
    const location = useLocation();
    const [form, setForm] = useState({ email: "", password: "" });
    const [error, setError] = useState("");
    const [submitting, setSubmitting] = useState(false);
    const [showPassword, setShowPassword] = useState(false);

    async function handleSubmit(event) {
        event.preventDefault();
        setError("");
        setSubmitting(true);
        try {
            await login(form.email, form.password);
            navigate(location.state?.from || "/", { replace: true });
        } catch (requestError) {
            setError(requestError.response?.data?.error || "Unable to sign in");
        } finally {
            setSubmitting(false);
        }
    }

    return (
        <Box sx={{ minHeight: "100vh", display: "grid", placeItems: "center", p: 3, bgcolor: "background.default", backgroundImage: "radial-gradient(circle at 50% 22%, rgba(140,198,63,.08), transparent 36%)" }}>
            <Stack spacing={2.5} alignItems="center" sx={{ width: "100%" }}>
                <Box component="img" src="/pbr-logo.png" alt="Point Blank Robotics" sx={{ display: "block", width: "100%", maxWidth: 370, height: 72, objectFit: "contain", objectPosition: "center", mixBlendMode: "screen" }} />
                <Paper component="main" elevation={0} sx={{ width: "100%", maxWidth: 480, p: { xs: 3, sm: 5 }, position: "relative", overflow: "hidden", borderColor: "rgba(140,198,63,.3)", boxShadow: "0 24px 80px rgba(0,0,0,.42)" }}>
                    <Box sx={{ position: "absolute", inset: 0, pointerEvents: "none", background: "radial-gradient(circle at 100% 0%, rgba(140,198,63,.18), transparent 42%), linear-gradient(135deg, transparent 65%, rgba(140,198,63,.035))" }} />
                    <Stack spacing={3} sx={{ position: "relative" }}>
                        <Typography variant="overline" color="primary.main" sx={{ display: "block", letterSpacing: ".2em", fontWeight: 800 }}>PBR CORE / INTERNAL PORTAL</Typography>
                        <Box>
                            <Typography variant="h4" sx={{ mb: 0.75 }}>Welcome to PBR Core.</Typography>
                            <Typography color="text.secondary">Sign in to your operations workspace.</Typography>
                        </Box>
                        {error && <Alert severity="error">{error}</Alert>}
                        <Box component="form" onSubmit={handleSubmit}>
                            <Stack spacing={2}>
                                <TextField required fullWidth label="Work email" type="email" autoComplete="username" value={form.email} onChange={(event) => setForm({ ...form, email: event.target.value })} />
                                <TextField
                                    required
                                    fullWidth
                                    label="Password"
                                    type={showPassword ? "text" : "password"}
                                    autoComplete="current-password"
                                    value={form.password}
                                    onChange={(event) => setForm({ ...form, password: event.target.value })}
                                    InputProps={{
                                        endAdornment: (
                                            <InputAdornment position="end">
                                                <IconButton aria-label={showPassword ? "Hide password" : "Show password"} onClick={() => setShowPassword((visible) => !visible)} edge="end">
                                                    {showPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
                                                </IconButton>
                                            </InputAdornment>
                                        ),
                                    }}
                                />
                                <Button type="submit" variant="contained" size="large" disabled={submitting}>{submitting ? "Signing in…" : "Sign in"}</Button>
                            </Stack>
                        </Box>
                        <Typography variant="caption" color="text.secondary">Authorized PBR staff only. Activity is protected and logged at the application boundary.</Typography>
                    </Stack>
                </Paper>
            </Stack>
        </Box>
    );
}
